/*
 * Visor de PDF en ventana emergente.
 *
 * Intercepta los clics en cualquier enlace que apunte a un archivo .pdf
 * (por ejemplo, los botones "Ver certificación") y muestra el documento en
 * una ventana emergente sobre la página, sin sacar al cliente del sitio.
 *
 * - Escritorio con visor de PDF nativo: se muestra en un iframe.
 * - Móviles (y navegadores sin visor nativo): se dibuja con PDF.js.
 *
 * Para excluir un enlace: agregar el atributo data-no-pdf-modal.
 * Para forzar un enlace que no termina en .pdf: agregar data-pdf-modal.
 * Para definir el título del visor: agregar data-pdf-title="...".
 */
(() => {
  if (customElements.get('pdf-viewer-modal')) return;

  const PDFJS_BASE = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/';
  const ZOOM_LEVELS = [1, 1.5, 2, 3];

  let pdfjsPromise = null;
  const loadPdfJs = () => {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    if (!pdfjsPromise) {
      pdfjsPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${PDFJS_BASE}pdf.min.js`;
        script.async = true;
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}pdf.worker.min.js`;
          resolve(window.pdfjsLib);
        };
        script.onerror = () => {
          pdfjsPromise = null;
          reject(new Error('No se pudo cargar PDF.js'));
        };
        document.head.appendChild(script);
      });
    }
    return pdfjsPromise;
  };

  const isMobileDevice = () =>
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const titleFromUrl = (url) => {
    try {
      const file = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
      return file.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').trim();
    } catch (e) {
      return '';
    }
  };

  class PdfViewerModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.titleEl = this.querySelector('[data-pdf-title]');
      this.body = this.querySelector('[data-pdf-body]');
      this.status = this.querySelector('[data-pdf-status]');
      this.externalLink = this.querySelector('[data-pdf-external]');
      this.zoomControls = this.querySelector('[data-pdf-zoom]');
      this.zoomLabel = this.querySelector('[data-pdf-zoom-label]');
      this.token = 0;

      this.querySelectorAll('[data-pdf-close]').forEach((button) =>
        button.addEventListener('click', () => this.close())
      );
      this.querySelector('[data-pdf-zoom-in]').addEventListener('click', () => this.setZoom(1));
      this.querySelector('[data-pdf-zoom-out]').addEventListener('click', () => this.setZoom(-1));

      // Clic fuera del panel (sobre el fondo) cierra la ventana.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.cleanup());

      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    onDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || link.hasAttribute('data-no-pdf-modal')) return;

      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (e) {
        return;
      }
      if (!/^https?:$/.test(url.protocol)) return;
      if (!link.hasAttribute('data-pdf-modal') && !/\.pdf$/i.test(url.pathname)) return;

      event.preventDefault();
      this.open(url.href, link.dataset.pdfTitle || titleFromUrl(url.href));
    }

    open(url, title) {
      const token = ++this.token;
      this.url = url;
      this.titleEl.textContent = title || '';
      this.externalLink.href = url;
      this.resetBody();

      if (!this.dialog.open) {
        this.previousFocus = document.activeElement;
        document.documentElement.classList.add('pdf-modal-open');
        this.dialog.showModal();
      }

      if (navigator.pdfViewerEnabled === true && !isMobileDevice()) {
        this.renderIframe(url);
      } else {
        this.renderWithPdfJs(url, token).catch(() => {
          if (token !== this.token) return;
          if (isMobileDevice()) {
            this.showStatus(this.dataset.errorText, true);
          } else {
            this.renderIframe(url);
          }
        });
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    cleanup() {
      this.token++;
      this.resetBody();
      document.documentElement.classList.remove('pdf-modal-open');
      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    resetBody() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.loadingTask) this.loadingTask.destroy();
      this.loadingTask = null;
      this.pdf = null;
      this.pages = [];
      this.zoomIndex = 0;
      this.zoomControls.hidden = true;
      this.body.querySelectorAll('.pdf-modal__frame, .pdf-modal__pages').forEach((el) => el.remove());
      this.showStatus(this.dataset.loadingText, false);
    }

    showStatus(text, isError) {
      this.status.hidden = !text;
      this.status.classList.toggle('pdf-modal__status--error', Boolean(isError));
      this.status.querySelector('[data-pdf-status-text]').textContent = text || '';
    }

    renderIframe(url) {
      const iframe = document.createElement('iframe');
      iframe.className = 'pdf-modal__frame';
      iframe.title = this.titleEl.textContent || 'PDF';
      iframe.src = `${url.split('#')[0]}#view=FitH`;
      iframe.addEventListener('load', () => this.showStatus('', false), { once: true });
      this.body.appendChild(iframe);
    }

    async renderWithPdfJs(url, token) {
      const pdfjsLib = await loadPdfJs();
      if (token !== this.token) return;

      this.loadingTask = pdfjsLib.getDocument({ url });
      const pdf = await this.loadingTask.promise;
      if (token !== this.token) return;
      this.pdf = pdf;

      const firstPage = await pdf.getPage(1);
      if (token !== this.token) return;
      const baseViewport = firstPage.getViewport({ scale: 1 });

      const container = document.createElement('div');
      container.className = 'pdf-modal__pages';
      this.pagesContainer = container;

      for (let number = 1; number <= pdf.numPages; number++) {
        const wrapper = document.createElement('div');
        // no-empty: el tema oculta los div vacíos (div:empty) y la página nunca se dibujaría.
        wrapper.className = 'pdf-modal__page no-empty';
        wrapper.style.aspectRatio = `${baseViewport.width} / ${baseViewport.height}`;
        wrapper.setAttribute('aria-label', `${number} / ${pdf.numPages}`);
        wrapper.dataset.page = number;
        container.appendChild(wrapper);
        this.pages.push({ number, wrapper, visible: false, renderedZoom: null, task: null });
      }

      this.body.appendChild(container);
      this.showStatus('', false);
      this.zoomControls.hidden = false;
      this.updateZoomLabel();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const page = this.pages[Number(entry.target.dataset.page) - 1];
            page.visible = entry.isIntersecting;
            if (page.visible) this.renderPage(page, token);
          });
        },
        { root: container, rootMargin: '100% 0px' }
      );
      this.pages.forEach((page) => this.observer.observe(page.wrapper));
    }

    async renderPage(page, token) {
      const zoom = ZOOM_LEVELS[this.zoomIndex];
      if (!this.pdf || page.renderedZoom === zoom) return;
      page.renderedZoom = zoom;
      if (page.task) page.task.cancel();

      try {
        const pdfPage = await this.pdf.getPage(page.number);
        if (token !== this.token) return;

        const unscaled = pdfPage.getViewport({ scale: 1 });
        page.wrapper.style.aspectRatio = `${unscaled.width} / ${unscaled.height}`;
        const cssWidth = page.wrapper.clientWidth || this.pagesContainer.clientWidth;
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = pdfPage.getViewport({ scale: (cssWidth / unscaled.width) * pixelRatio });

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        page.task = pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport });
        await page.task.promise;
        page.task = null;
        if (token !== this.token) return;

        page.wrapper.replaceChildren(canvas);
      } catch (error) {
        if (error && error.name === 'RenderingCancelledException') return;
        page.renderedZoom = null;
      }
    }

    setZoom(direction) {
      const next = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, this.zoomIndex + direction));
      if (next === this.zoomIndex || !this.pagesContainer) return;

      const container = this.pagesContainer;
      const ratioY = container.scrollTop / Math.max(1, container.scrollHeight);
      this.zoomIndex = next;
      container.style.setProperty('--pdf-zoom', ZOOM_LEVELS[next]);
      container.scrollTop = ratioY * container.scrollHeight;
      this.updateZoomLabel();

      const token = this.token;
      this.pages.forEach((page) => {
        if (page.visible) this.renderPage(page, token);
        else page.renderedZoom = null;
      });
    }

    updateZoomLabel() {
      this.zoomLabel.textContent = `${Math.round(ZOOM_LEVELS[this.zoomIndex] * 100)}%`;
      this.querySelector('[data-pdf-zoom-out]').disabled = this.zoomIndex === 0;
      this.querySelector('[data-pdf-zoom-in]').disabled = this.zoomIndex === ZOOM_LEVELS.length - 1;
    }
  }

  customElements.define('pdf-viewer-modal', PdfViewerModal);
})();
